package us.laantech.admin.util

import kotlinx.serialization.KSerializer
import kotlinx.serialization.descriptors.SerialDescriptor
import kotlinx.serialization.descriptors.SerialKind
import kotlinx.serialization.descriptors.StructureKind
import kotlinx.serialization.json.*

/**
 * Shared Json config. `encodeDefaults = true` makes sure every field
 * is present in the map even if it has a default, so the form renders all of them.
 */
val FormJson = Json {
    encodeDefaults = true
    explicitNulls = true
    ignoreUnknownKeys = true
}

/** Description of one form field that the UI can render. */
data class FormField(
    val name: String,            // serial name (e.g. "accountSub")
    val kind: SerialKind,        // PrimitiveKind.STRING, StructureKind.CLASS, LIST, ...
    val descriptor: SerialDescriptor,
    val isNullable: Boolean,
)

/** Walk one level of a descriptor and return its fields. */
fun formFieldsOf(descriptor: SerialDescriptor): List<FormField> =
    (0 until descriptor.elementsCount).map { i ->
        val child = descriptor.getElementDescriptor(i)
        FormField(
            name = descriptor.getElementName(i),
            kind = child.kind,
            descriptor = child,
            isNullable = child.isNullable,
        )
    }

/** T  ➜  JsonObject  ➜  MutableMap<String, JsonElement?> for editing. */
fun <T> serializableToMap(serializer: KSerializer<T>, value: T): MutableMap<String, JsonElement> {
    val element = FormJson.encodeToJsonElement(serializer, value)
    require(element is JsonObject) { "Top-level value must be a class/object" }
    return element.toMutableMap()
}

/** MutableMap<String, JsonElement>  ➜  T. Throws if invalid. */
fun <T> mapToSerializable(serializer: KSerializer<T>, map: Map<String, JsonElement>): T {
    val obj = JsonObject(map)
    return FormJson.decodeFromJsonElement(serializer, obj)
}

/** Build an "empty" JsonObject for a class descriptor (all fields → JsonNull/defaults). */
fun emptyJsonObjectFor(descriptor: SerialDescriptor): JsonObject = buildJsonObject {
    require(descriptor.kind == StructureKind.CLASS) { "Need a class descriptor" }
    for (i in 0 until descriptor.elementsCount) {
        val child = descriptor.getElementDescriptor(i)
        val default: JsonElement = when {
            child.isNullable -> JsonNull
            child.kind == StructureKind.LIST -> JsonArray(emptyList())
            child.kind == StructureKind.CLASS -> emptyJsonObjectFor(child)
            else -> JsonNull
        }
        put(descriptor.getElementName(i), default)
    }
}

/** Convenience helpers for primitive editing. */
fun JsonElement?.asStringOrEmpty(): String = when (this) {
    null, JsonNull -> ""
    is JsonPrimitive -> content
    else -> toString()
}

fun String.toJsonStringElement(nullable: Boolean): JsonElement =
    if (nullable && this.isEmpty()) JsonNull else JsonPrimitive(this)

fun String.toJsonNumberElement(nullable: Boolean): JsonElement =
    if (this.isEmpty()) {
        if (nullable) JsonNull else JsonPrimitive(0)
    } else {
        this.toLongOrNull()?.let(::JsonPrimitive)
            ?: this.toDoubleOrNull()?.let(::JsonPrimitive)
            ?: JsonPrimitive(this) // let decoder fail with a clear error
    }

fun Boolean.toJsonElement(): JsonElement = JsonPrimitive(this)